import React, { useState } from "react";
import PropTypes from "prop-types";
import { Avatar, Box, Link, Typography } from "@mui/material";
import common from "../../utils/common";
import AmbassadorHelper from "../../utils/AmbassadorHelper";

const MAX_PRODUCT_CHIPS = 3;

const COLUMN_STEP_MS = 90;
const ROW_STEP_MS = 40;
const MAX_REVEAL_DELAY_MS = 480;
const EAGER_TILE_COUNT = 4;

const SIZES = {
    rail: { arrow: [18, 13], strip: "6px", chip: 22, pillFont: "9px", thumb: 480 },
    masonry: { arrow: [26, 19], strip: "10px", chip: 34, pillFont: "12px", thumb: 1080 },
};

const formatDuration = (duration) => {
    if (!duration && duration !== 0) return "";
    if (typeof duration === "string" && duration.includes(":")) return duration;
    const seconds = Number(duration);
    if (!Number.isFinite(seconds) || seconds <= 0) return "";
    const minutes = Math.floor(seconds / 60);
    return `${minutes}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;
};

const formatPostedOn = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const PlayGlyph = ({ size }) => {
    const [left, edge] = size.arrow;
    return (
        <Box
            component="span"
            sx={{
                position: "absolute",
                left: "50%",
                top: "50%",
                width: 0,
                height: 0,
                margin: `-${edge}px 0 0 -${left / 2}px`,
                borderLeft: `${left}px solid #FFFFFF`,
                borderTop: `${edge}px solid transparent`,
                borderBottom: `${edge}px solid transparent`,
                filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.35))",
                display: "block",
                pointerEvents: "none",
            }}
        />
    );
};

const PostTile = ({ asset, ratio = "3 / 4", mobileView = false, redirect = true, index, column = 0, row = 0, onClickItem, showCreator = false, width, variant = "masonry", card = false }) => {
    const [hovered, setHovered] = useState(false);
    const position = index || 0;
    const aboveFold = position < EAGER_TILE_COUNT;
    const [loaded, setLoaded] = useState(false);
    const revealDelay = Math.min(column * COLUMN_STEP_MS + row * ROW_STEP_MS, MAX_REVEAL_DELAY_MS);
    const size = SIZES[variant] || SIZES.masonry;
    const assetUrl = common.generateAssetUrl(asset);
    const thumbnail = common.getThumbnail(asset, size.thumb);
    const isVideo = asset.entityType === 1;
    const products = asset.products || [];
    const chips = products.slice(0, MAX_PRODUCT_CHIPS);
    const overflow = products.length - chips.length;
    const creator = asset.creator || {};
    const creatorOverlay = showCreator && Boolean(creator.full_name);
    const duration = isVideo ? formatDuration(asset.duration) : "";
    const assetLabel = asset.title || asset.description || "";
    const linkLabel = creatorOverlay ? [creator.full_name, assetLabel].filter(Boolean).join(" \u2014 ") : assetLabel;
    const postedOn = formatPostedOn(asset.creation_date);

    const onClick = (event) => {
        if (redirect === false) {
            event.preventDefault();
            event.stopPropagation();
            if (typeof onClickItem === "function") {
                onClickItem({ item: asset, index });
            }
        }
    };

    const wrapperSx = () => {
        if (width) {
            return { position: "relative", flex: "0 0 auto", width, scrollSnapAlign: "start" };
        }
        if (card) {
            return {
                position: "relative",
                margin: "0 0 18px",
                breakInside: "avoid",
                WebkitColumnBreakInside: "avoid",
                backgroundColor: (theme) => theme.tvp.card,
                border: "1px solid #EDE6E1",
                borderRadius: (theme) => theme.tvp.radiusCard,
                overflow: "hidden",
            };
        }
        return { position: "relative", margin: "0 0 10px", breakInside: "avoid", WebkitColumnBreakInside: "avoid" };
    };

    return (
        <Box
            sx={wrapperSx()}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <Link
                href={assetUrl}
                onClick={onClick}
                underline="none"
                aria-label={linkLabel || undefined}
                sx={{
                    display: "block",
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: (theme) => (card ? 0 : theme.tvp.radiusMedia),
                    aspectRatio: ratio,
                    backgroundColor: (theme) => theme.tvp.surface,
                }}
            >
                <Box
                    component="img"
                    src={thumbnail}
                    alt=""
                    loading={aboveFold ? "eager" : "lazy"}
                    fetchpriority={position === 0 ? "high" : undefined}
                    decoding="async"
                    onLoad={() => setLoaded(true)}
                    sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center",
                        opacity: loaded ? 1 : 0,
                        transform: hovered ? "scale(1.04)" : "scale(1)",
                        transition: "opacity 320ms ease, transform 260ms ease",
                        transitionDelay: `${revealDelay}ms, 0ms`,
                    }}
                />
                {duration && (
                    <Box
                        component="span"
                        sx={{
                            position: "absolute",
                            right: "8px",
                            top: "8px",
                            display: "flex",
                            alignItems: "center",
                            padding: "3px 9px",
                            borderRadius: "999px",
                            backgroundColor: "rgba(0,0,0,0.62)",
                            color: "#fff",
                            fontSize: "11px",
                            fontWeight: 700,
                            letterSpacing: "0.2px",
                            pointerEvents: "none",
                        }}
                    >
                        {duration}
                    </Box>
                )}
                {isVideo && <PlayGlyph size={size} />}
                {creatorOverlay && (
                    <Box
                        component="span"
                        sx={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            bottom: 0,
                            padding: "8px",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            background: "linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0) 100%)",
                            pointerEvents: "none",
                        }}
                    >
                        <Avatar
                            src={AmbassadorHelper.getCreatorPhoto(creator)}
                            alt=""
                            sx={{ width: 22, height: 22, flex: "none", boxShadow: "0 0 0 1.5px rgba(255,255,255,0.9)" }}
                        />
                        <Box
                            component="span"
                            sx={{
                                minWidth: 0,
                                fontSize: "11px",
                                fontWeight: 700,
                                color: "#fff",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {creator.full_name}
                        </Box>
                    </Box>
                )}
                {!creatorOverlay && chips.length > 0 && (
                    <Box
                        component="span"
                        sx={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            bottom: 0,
                            padding: size.strip,
                            display: "flex",
                            gap: "4px",
                            alignItems: "flex-end",
                            background: "linear-gradient(to top, rgba(0,0,0,0.42), rgba(0,0,0,0) 100%)",
                            pointerEvents: "none",
                        }}
                    >
                        {chips.map((product) => (
                            <Box
                                component="span"
                                key={product.product_id}
                                title={product.title}
                                sx={{
                                    width: `${size.chip}px`,
                                    height: `${size.chip}px`,
                                    flex: "none",
                                    borderRadius: "4px",
                                    backgroundColor: "#fff",
                                    backgroundImage: `url(${common.adjustImageUrl(product.image)})`,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    backgroundRepeat: "no-repeat",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
                                }}
                            />
                        ))}
                        {overflow > 0 && (
                            <Box
                                component="span"
                                sx={{
                                    height: `${size.chip}px`,
                                    padding: "0 5px",
                                    borderRadius: "4px",
                                    backgroundColor: "rgba(255,255,255,0.92)",
                                    color: "#111",
                                    fontSize: size.pillFont,
                                    fontWeight: 700,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {`+${overflow}`}
                            </Box>
                        )}
                    </Box>
                )}
            </Link>
            {card && (
                <Box sx={{ padding: "13px 14px 14px", display: "flex", flexDirection: "column", gap: "11px" }}>
                    {creator.full_name && (
                        <Link
                            href={common.generateCreatorUrl(creator) || undefined}
                            underline="none"
                            sx={{ display: "flex", alignItems: "center", gap: "9px", minWidth: 0 }}
                        >
                            <Avatar
                                src={AmbassadorHelper.getCreatorPhoto(creator)}
                                alt=""
                                sx={{ width: 30, height: 30, flex: "none" }}
                            />
                            <Typography
                                fontSize="13px"
                                fontWeight={700}
                                color="#1A1A1A"
                                sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            >
                                {creator.full_name}
                            </Typography>
                            {postedOn && (
                                <Typography fontSize="11px" color={(theme) => theme.tvp.textSubtle} sx={{ marginLeft: "auto", flex: "none" }}>
                                    {postedOn}
                                </Typography>
                            )}
                        </Link>
                    )}
                    {asset.title && (
                        <Typography fontSize="13.5px" fontWeight={600} lineHeight={1.35} color="#1A1A1A">
                            {asset.title}
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
};


PostTile.propTypes = {
    asset: PropTypes.object.isRequired,
    ratio: PropTypes.string,
    redirect: PropTypes.bool,
    showCreator: PropTypes.bool,
    card: PropTypes.bool,
    variant: PropTypes.oneOf(["rail", "masonry"]),
};

export default PostTile;
